import {
  eintraegeVon,
  eintragFelder,
  festerSlot,
  loeseSlot,
  menueWerte,
  type Aufgeloest,
  type Daten,
  type Verweise,
} from "./belegung.ts";
import { blaetterSlots, slotAnzahl } from "./form.ts";
import type { Katalog, SlotFelder, UiDaten, Werte } from "./modell.ts";
import { ersetze, ersetzeZeilen } from "./platzhalter.ts";

export type Knopf = {
  name: string;
  zeile?: string;
  bild?: string;
  aktion?: string;
  wert?: string;
  bestaetigung?: string;
  navigation?: "zurueck" | "weiter";
};
export type FormularSeite = { titel: string; inhalt: string[]; knoepfe: Knopf[] };

const PRO_SEITE = 20;

/** Einträge je Seite neben `fest` festen Knöpfen; null, wenn Bedrock das nicht unterbringt. */
export function seitenAufteilung(fest: number, eintraege: number): number[] | null {
  if (fest + eintraege <= PRO_SEITE) return [eintraege];
  const seiten: number[] = [];
  let rest = eintraege;
  while (rest > 0) {
    const vorlaeufig = PRO_SEITE - fest - (seiten.length === 0 ? 0 : 1);
    if (seiten.length > 0 && rest <= vorlaeufig) {
      seiten.push(rest);
      break;
    }
    const kapazitaet = vorlaeufig - 1;
    if (kapazitaet < 1) return null;
    seiten.push(Math.min(kapazitaet, rest));
    rest -= kapazitaet;
  }
  return seiten;
}

function knopf(a: Aufgeloest, katalog: Katalog): Knopf {
  const k: Knopf = { name: a.name };
  const zeile = a.beschreibung[0];
  if (zeile !== undefined) k.zeile = zeile;
  const bild = a.bedrockBild ?? katalog.bedrockBilder[a.symbol];
  if (bild) k.bild = bild;
  if (a.aktion) k.aktion = a.aktion;
  if (a.wert !== undefined) k.wert = a.wert;
  if (a.bestaetigung) k.bestaetigung = a.bestaetigung;
  return k;
}

function navigation(art: "zurueck" | "weiter", katalog: Katalog): Knopf {
  const k: Knopf = { name: art === "zurueck" ? "« Zurück" : "Weiter »" };
  const bild = katalog.bedrockBilder["minecraft:arrow"];
  if (bild) k.bild = bild;
  k.navigation = art;
  return k;
}

type Roh = { felder: SlotFelder; werte: Werte };

export function bedrockSeiten(ui: UiDaten, katalog: Katalog, daten: Daten, verweise?: Verweise): FormularSeite[] {
  const n = slotAnzahl(ui.form);
  const blaettern = new Set(blaetterSlots(ui));
  const listenSlots = ui.listen.flatMap((l) => l.slots);
  const grenze = listenSlots.length > 0 ? Math.min(...listenSlots) : Number.POSITIVE_INFINITY;

  const feste = Object.keys(ui.slots)
    .map(Number)
    .filter((i) => i < n && !blaettern.has(i))
    .sort((a, b) => a - b)
    .flatMap((i) => {
      const felder = festerSlot(ui, i, daten);
      return felder ? [{ i, felder }] : [];
    });
  const vorher = feste.filter((f) => f.i < grenze);
  const nachher = feste.filter((f) => f.i >= grenze);

  const eintraege: { felder: SlotFelder; werte: Werte | null }[] = [];
  for (const l of ui.listen) {
    const alle = eintraegeVon(l, daten);
    if (alle.length === 0 && l.leer) eintraege.push({ felder: l.leer, werte: null });
    for (const e of alle) eintraege.push({ felder: eintragFelder(l, e.zustand), werte: e.werte });
  }

  const aufteilung = ui.listen.length === 0 ? [0] : (seitenAufteilung(feste.length, eintraege.length) ?? [eintraege.length]);
  const anzahl = aufteilung.length;
  const seiten: FormularSeite[] = [];
  let start = 0;
  aufteilung.forEach((menge, p) => {
    const werte = menueWerte(ui, daten, p, anzahl);
    const roh: Roh[] = [
      ...vorher.map((f) => ({ felder: f.felder, werte })),
      ...eintraege.slice(start, start + menge).map((e) => ({ felder: e.felder, werte: { ...werte, ...(e.werte ?? {}) } })),
      ...nachher.map((f) => ({ felder: f.felder, werte })),
    ];
    start += menge;
    const aufgeloest = roh.map((r) => loeseSlot(r.felder, r.werte, katalog, verweise));
    const knoepfe: Knopf[] = [];
    if (p > 0) knoepfe.push(navigation("zurueck", katalog));
    knoepfe.push(...aufgeloest.map((a) => knopf(a, katalog)));
    if (p < anzahl - 1) knoepfe.push(navigation("weiter", katalog));
    const inhalt = [
      ...ersetzeZeilen(ui.text, werte),
      ...aufgeloest.filter((a) => a.beschreibung.length > 1).map((a) => [a.name, ...a.beschreibung].join("\n")),
    ];
    const titel = ersetze(ui.titel, werte) + (anzahl > 1 ? ` (${p + 1}/${anzahl})` : "");
    seiten.push({ titel, inhalt, knoepfe });
  });
  return seiten;
}
